import React, { useEffect, useState, useMemo } from "react";
import {
    Container, Card, CardBody, CardTitle, Row, Col, FormGroup, Label, Input,
    Button, Alert, Spinner, Table, Collapse, Badge,
    Modal,
    ModalHeader,
    ModalBody
} from "reactstrap";
import AuthFunctions from '../../services/AuthService';
import Session from '../../auth/Session';
import { useNavigate } from 'react-router-dom';
import * as dayjs from 'dayjs';
import InvoiceDetailForm from "./InvoiceDetailForm";
import InvoiceAdjustmentForm from "./InvoiceAdjustmentForm";
import { getFinanceFilters, saveFinanceFilter } from "./financeFilterStorage";
import SearchableDropdownFilter from "../SearchableDropdownFilter";

const getCustomerLabel = (customer) => customer.name;
const getSubCustomerLabel = (subCustomer) =>
    `${subCustomer.name}${subCustomer.customer ? ` — ${subCustomer.customer.name}` : ""}`;
const getOptionId = (option) => option.id;
const isOvertimeLine = (detail) => detail?.surchargeId === null && Number(detail?.expectedDays ?? 0) === 0 && Number(detail?.overtimeHours ?? 0) > 0;
const getOvertimeCutoff = (route) => {
    if (!route) return 0;
    if (route.billingType === "Half-day") return Number(route.halfDayOT ?? 0);
    return Number(route.fullDayOT ?? 0);
};
const isOvertimeCutoffValidationLine = (detail) => detail?.surchargeId === null
    && detail?.route?.allowOvertime === true
    && getOvertimeCutoff(detail.route) <= 0
    && Number(detail?.expectedDays ?? 0) === 0
    && Number(detail?.overtimeHours ?? 0) === 0
    && Number(detail?.billRate ?? 0) === 0;
const isFuelSurchargeLine = (detail) => Number(detail?.surcharge?.surchargeTypeId ?? 0) === 5
    || `${detail?.surcharge?.surchargeType?.name ?? ""}`.toLowerCase().includes("fuel");
const isPercentageSurchargeLine = (detail) => Number(detail?.surcharge?.surchargeTypeId ?? 0) === 2
    || `${detail?.surcharge?.surchargeType?.name ?? ""}`.toLowerCase() === "percentage";
const isFuelSurchargeValidationLine = (detail) => detail?.hasValidationError === true
    && isFuelSurchargeLine(detail);
const isZeroCalculatedFuelSurchargeLine = (detail) => isFuelSurchargeLine(detail)
    && Number(detail?.billRate ?? 0) <= 0
    && Number(detail?.lineTotal ?? 0) <= 0;
const isAdjustmentLine = (detail) => detail?.surchargeId == null && detail?.transactionTypeId != null;
// A "False Trip" line shares its route's routeId. It may be the auto-generated flat line
// (description "False Trip", no transaction type) or a manually added / classification-sourced
// line carrying a "False Trip" transaction type; recognize both so the label and QBO item stay
// tied to the route.
const isFalseTripLine = (detail) => detail?.routeId != null && detail?.surchargeId == null
    && (`${detail?.description ?? ""}`.trim().toLowerCase().startsWith("false trip")
        || `${detail?.transactionType?.transactionName ?? ""}`.toLowerCase().includes("false trip"));
// The route-less "Backup Driver" standby line — like an adjustment (no route/surcharge) but with no
// transaction type; identified by its description.
const isBackupDriverLine = (detail) => detail?.routeId == null && detail?.surchargeId == null
    && detail?.transactionTypeId == null
    && `${detail?.description ?? ""}`.trim().toLowerCase() === "backup driver";
// The route-less backup "make-up" line — route-less with no transaction type; identified by its
// description.
const isBackupMakeupLine = (detail) => detail?.routeId == null && detail?.surchargeId == null
    && detail?.transactionTypeId == null
    && `${detail?.description ?? ""}`.trim().toLowerCase() === "backup make-up";
// The route-less "Lead Pay" management line — like the backup line, route-less with no transaction
// type; identified by its description.
const isLeadDriverLine = (detail) => detail?.routeId == null && detail?.surchargeId == null
    && detail?.transactionTypeId == null
    && `${detail?.description ?? ""}`.trim().toLowerCase() === "lead pay";
const isBaseLine = (detail) => detail?.surchargeId === null && detail?.routeId != null && !isAdjustmentLine(detail) && !isFalseTripLine(detail) && !isBackupDriverLine(detail) && !isBackupMakeupLine(detail) && !isLeadDriverLine(detail) && !isOvertimeLine(detail) && !isOvertimeCutoffValidationLine(detail);
const isHourlyBaseLine = (detail) => isBaseLine(detail) && detail?.route?.billingType === "Hourly";
const lineDescription = (detail) => isFalseTripLine(detail)
    ? "False Trip"
    : isAdjustmentLine(detail)
    ? (detail.description || detail.transactionType?.transactionName || "Adjustment")
    : isBackupDriverLine(detail)
    ? "Backup Driver"
    : isBackupMakeupLine(detail)
    ? "Backup Driver"
    : isLeadDriverLine(detail)
    ? "Lead Pay"
    : isFuelSurchargeValidationLine(detail) || isZeroCalculatedFuelSurchargeLine(detail)
    ? (detail.validationMessage || "Error: fuel rate is missing or zero")
    : isOvertimeCutoffValidationLine(detail)
    ? "Error: additional drive time cutoff is missing"
    : isOvertimeLine(detail)
    ? "Additional drive time"
    : isBaseLine(detail)
        ? detail.route?.transactionType?.transactionName
        : detail.surcharge?.description;
const roundMoney = (val) => Math.round((Number(val ?? 0) + Number.EPSILON) * 100) / 100;
const lineTotal = (detail) => Number(detail?.lineTotal ?? (isOvertimeLine(detail)
    ? (Number(detail?.overtimeAmount ?? 0) || Number(detail?.billRate ?? 0) * Number(detail?.overtimeHours ?? 0))
    : Number(detail?.billRate ?? 0) * Number(detail?.daysOnRoute ?? 0) + Number(detail?.overtimeAmount ?? 0)));
const lineQuantity = (detail, validationLine, overtimeLine) => {
    if (validationLine) return "Error";
    if (isPercentageSurchargeLine(detail)) return `${Number(detail?.surcharge?.percentage ?? 0).toFixed(2)}%`;
    if (overtimeLine && detail?.route?.roundOvertimeToHalfHour === true) {
        const halfHours = Math.round(Number(detail?.daysOnRoute ?? 0) * 2);
        return `${halfHours} × ½hr`;
    }
    if (isHourlyBaseLine(detail)) {
        return `${Number(detail?.daysOnRoute ?? 0).toFixed(2)} hrs`;
    }
    return Number(detail?.daysOnRoute ?? 0).toFixed(2);
};
const getInvoiceRowCategory = (detail) => isBaseLine(detail) ? 0 : (isFalseTripLine(detail) || isBackupDriverLine(detail) || isBackupMakeupLine(detail) || isLeadDriverLine(detail) || isOvertimeLine(detail) || isOvertimeCutoffValidationLine(detail) ? 1 : (isAdjustmentLine(detail) ? 3 : 2));
const compareInvoiceRows = (a, b) => {
    const aSubCustomer = a.invoice?.subCustomer?.name || `${a.invoice?.subCustomerId ?? ''}`;
    const bSubCustomer = b.invoice?.subCustomer?.name || `${b.invoice?.subCustomerId ?? ''}`;
    if (aSubCustomer !== bSubCustomer) return aSubCustomer.localeCompare(bSubCustomer);

    // Lines without a route (adjustments) sort to the bottom of the
    // sub-customer group, after all routed lines. We can't rely on a
    // placeholder name here because localeCompare treats punctuation as
    // low-priority and would sort it before real route names.
    const aHasRoute = a.route?.name != null;
    const bHasRoute = b.route?.name != null;
    if (aHasRoute !== bHasRoute) return aHasRoute ? -1 : 1;

    if (aHasRoute) {
        const aRoute = a.route.name;
        const bRoute = b.route.name;
        if (aRoute !== bRoute) return aRoute.localeCompare(bRoute);
    }

    const aCategory = getInvoiceRowCategory(a);
    const bCategory = getInvoiceRowCategory(b);
    if (aCategory !== bCategory) return aCategory - bCategory;

    return (a.id ?? 0) - (b.id ?? 0);
};

const InvoiceReview = () => {
    const navigate = useNavigate();
    const savedFilters = getFinanceFilters();
    const customersApi = "/api/customers";
    const subCustomersApi = (customerId) => `/api/subcustomers/byCustomer/${customerId}`;
    const loadApi = (customerId, endingDate, subCustomerId = '') => `/api/invoicing/for-customer?customerId=${customerId}&endingDate=${endingDate}&type=review${subCustomerId ? `&subCustomerId=${subCustomerId}` : ''}`;
    const generateApi = (customerId, subcustomerId, endingDate) => `/api/invoicing/generate?customerId=${customerId}&subcustomerId=${subcustomerId}&endingDate=${endingDate}`;
    const routeWeeksApi = "/api/routes/invoice-weeks";
    const deleteSurchargeApi = (invoiceId, invoiceDetailId) => `/api/invoicing/${invoiceId}/delete-detail/${invoiceDetailId}`
    const submitApi = (invoiceId) => `/api/invoicing/${invoiceId}/submit/true`;
    const submitOverrideApi = (invoiceId) => `/api/invoicing/${invoiceId}/submit-override`;
    const removeRouteApi = (invoiceId, routeId) => `/api/invoicing/${invoiceId}/remove-route/${routeId}`;
    const [customers, setCustomers] = useState([]);
    const [subCustomers, setSubCustomers] = useState([]);
    const [subCustomerId, setSubCustomerId] = useState(savedFilters.subCustomerId || "");
    const [customerId, setCustomerId] = useState(savedFilters.customerId || "");
    const [loading, setLoading] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [error, setError] = useState("");
    const [weeks, setWeeks] = useState([
        { endingDate: '', displayDate: '' }
    ]);
    const [selectedWeek, setSelectedWeek] = useState(savedFilters.selectedWeek || '');
    const [addOpen, setAddOpen] = useState(false);
    const [adjustmentOpen, setAdjustmentOpen] = useState(false);
    const [adjustmentInvoiceId, setAdjustmentInvoiceId] = useState(null);
    const [adjustmentEditing, setAdjustmentEditing] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [overrideReason, setOverrideReason] = useState("");
    const [overrideSubmitting, setOverrideSubmitting] = useState(false);
    const [removeRouteOpen, setRemoveRouteOpen] = useState(false);
    const [removeRouteReason, setRemoveRouteReason] = useState("");
    const [removeRouteSubmitting, setRemoveRouteSubmitting] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editingSubCustomerId, setEditingSubCustomerId] = useState(null);

    // drill-down state
    const [openRows, setOpenRows] = useState(new Set());
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesText, setNotesText] = useState("");
    // "Change Route" modal state (reassign a route line to another route, e.g. its No-Delivery sibling).
    const [changeRouteOpen, setChangeRouteOpen] = useState(false);
    const [changeRouteDetail, setChangeRouteDetail] = useState(null);
    const [changeRouteOptions, setChangeRouteOptions] = useState([]);
    const [changeRouteValue, setChangeRouteValue] = useState("");
    const [changeRouteLoading, setChangeRouteLoading] = useState(false);
    const [changeRouteSubmitting, setChangeRouteSubmitting] = useState(false);
    // Combined route notes (paysheet col E + invoicing col F) for the route-row Notes modal.
    const routeNotesText = (line) => [
        line?.paysheetNotes ? `Paysheet notes:\n${line.paysheetNotes}` : null,
        line?.invoicingNotes ? `Invoicing notes:\n${line.invoicingNotes}` : null
    ].filter(Boolean).join("\n\n");
    const [detailsCache, setDetailsCache] = useState({}); // invoiceId -> details[]
    const [loadingDetails, setLoadingDetails] = useState({}); // invoiceId -> boolean

    useEffect(() => {
        const loadData = async () => {
            const token = await Session.getToken();
            fetch(routeWeeksApi, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setWeeks).catch(() => setWeeks([]));
            fetch(customersApi, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setCustomers).catch(() => setCustomers([]));
        };
        loadData();
    }, []);

    const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
    const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Fuel-surcharge rates are per-mile and need 3 decimals to read accurately (e.g. 0.119).
    const rateMoney = (detail) => {
        const digits = isFuelSurchargeLine(detail) ? 3 : 2;
        return (detail?.billRate ?? 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    };

    const sortedFilteredInvoices = useMemo(() => [...filteredInvoices].sort(compareInvoiceRows), [filteredInvoices]);

    // Active sub-customers plus any (possibly inactive) sub-customer that has invoice
    // data for the selected week, so inactive ones with existing data remain selectable.
    const subCustomerOptions = useMemo(() => {
        const map = new Map();
        for (const sc of subCustomers) map.set(`${sc.id}`, sc);
        for (const d of invoices) {
            const sc = d.invoice?.subCustomer;
            if (sc && !map.has(`${sc.id}`)) map.set(`${sc.id}`, sc);
        }
        return [...map.values()];
    }, [subCustomers, invoices]);

    const firstDetailIdBySubCustomer = useMemo(() => {
        const map = new Map();
        for (const invoice of sortedFilteredInvoices) {
            const subId = invoice.invoice?.subCustomerId;
            if (subId != null && !map.has(subId)) {
                map.set(subId, invoice.id);
            }
        }
        return map;
    }, [sortedFilteredInvoices]);

    const firstDetailIdBySubCustomerRoute = useMemo(() => {
        const map = new Map();
        for (const invoice of sortedFilteredInvoices) {
            const subId = invoice.invoice?.subCustomerId;
            const routeId = invoice.routeId ?? invoice.route?.id;
            const key = `${subId ?? ''}|${routeId ?? ''}`;
            if (!map.has(key)) {
                map.set(key, invoice.id);
            }
        }
        return map;
    }, [sortedFilteredInvoices]);

    const validationByInvoiceId = useMemo(() => {
        const map = new Map();
        for (const detail of invoices) {
            const invoiceId = detail.invoiceId;
            if (!map.has(invoiceId)) {
                map.set(invoiceId, true);
            }

            const mismatch = isFuelSurchargeValidationLine(detail)
                || isZeroCalculatedFuelSurchargeLine(detail)
                || isOvertimeCutoffValidationLine(detail)
                || (isBaseLine(detail) && !isHourlyBaseLine(detail) && detail.daysOnRoute !== detail.expectedDays);
            if (mismatch) {
                map.set(invoiceId, false);
            }
        }

        return map;
    }, [invoices]);

    const displayTotalByInvoiceId = useMemo(() => {
        const map = new Map();
        for (const detail of invoices) {
            const invoiceId = detail.invoiceId;
            map.set(invoiceId, (map.get(invoiceId) ?? 0) + roundMoney(lineTotal(detail)));
        }
        return map;
    }, [invoices]);

    useEffect(() => {
        let filtered = [];

        if (subCustomerId) {
            filtered = invoices.filter(i => `${i?.invoice?.subCustomerId}` === subCustomerId);
        } else {
            filtered = [...invoices];
        }

        setFilteredInvoices(filtered);
    }, [invoices, subCustomerId])

    useEffect(() => {
        let filters = getFinanceFilters();
        let selected = filters.selectedWeek
            ? weeks.find(w => w.endingDate === filters.selectedWeek)
            : weeks.find(w => w.selected === true);
        if (selected) {
            setSelectedWeek(selected.endingDate);
        }
    }, [weeks]);

    // load existing invoices for prior week on customer change
    useEffect(() => {
        const run = async () => {
            var isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) {
                navigate('/logout');
            }
            else {
                if (!customerId) {
                    setSchedule(null); setInvoices([]); setOpenRows(new Set()); setDetailsCache({});
                    return;
                }
                setLoading(true); setError("");
                try {
                    const token = await Session.getToken();
                    const invoiceDate = selectedWeek ? dayjs(selectedWeek, 'MM/DD/YYYY').format('YYYY-MM-DD') : dayjs().subtract(7, 'day').format("YYYY-MM-DD");
                    const [res, subcustomers] = await Promise.all([
                        fetch(loadApi(customerId, invoiceDate, subCustomerId), { headers: { Authorization: `Bearer ${token}` } }),
                        fetch(subCustomersApi(customerId), { headers: { Authorization: `Bearer ${token}` } })
                    ]);
                    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                    const data = await res.json();
                    setSchedule(data.schedule);
                    setInvoices(data.invoices || []);
                    setSubCustomers(subcustomers.ok ? await subcustomers.json() : []);
                } catch (e) {
                    setError(e.message || "Failed to load invoices.");
                } finally {
                    setLoading(false);
                }
            }
        };
        run();
    }, [customerId, navigate, selectedWeek, subCustomerId]);

    const validateInvoice = (invoice) => validationByInvoiceId.get(invoice.invoiceId) ?? true;

    const openAdd = async (invoice, subCustomerId) => {
        setEditing(invoice);
        setEditingSubCustomerId(subCustomerId);
        setAddOpen(true);
    }

    const openDelete = async (invoice) => {
        setEditing(invoice);
        setDeleteOpen(true);
    }

    const openRemoveRoute = (invoice) => {
        setEditing(invoice);
        setRemoveRouteReason("");
        setRemoveRouteOpen(true);
    }

    const handleRemoveRoute = async () => {
        const detail = editing;
        const routeId = detail?.routeId ?? detail?.route?.id;
        if (!detail || routeId == null) return;
        setRemoveRouteSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(removeRouteApi(detail.invoiceId, routeId), {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ reason: removeRouteReason.trim() }),
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
            setRemoveRouteOpen(false);
        } catch (e) {
            setError(e.message || "Failed to remove route from invoice.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setRemoveRouteSubmitting(false);
        }
    }
    const openSubmit = (invoice) => {
        setEditing(invoice);
        const isValid = validationByInvoiceId.get(invoice.invoiceId) ?? true;
        if (isValid) {
            setSubmitOpen(true);
        } else {
            setOverrideReason("");
            setOverrideOpen(true);
        }
    };

    const handleSubmitWithOverride = async () => {
        if (!overrideReason.trim()) return;
        const detail = editing;
        if (!detail) return;
        setOverrideSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(submitOverrideApi(detail.invoiceId), {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ reason: overrideReason.trim() }),
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
            setOverrideOpen(false);
        } catch (e) {
            setError(e.message || "Failed to submit invoice.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setOverrideSubmitting(false);
        }
    };

    const submitAddChild = async () => {
        // if (!schedule?.id || !addForRouteId || !newDriverId) return;
        // try {
        //     const res = await fetch(createChildUrl, {
        //         method: "POST",
        //         headers: { "Content-Type": "application/json" },
        //         body: JSON.stringify({
        //             scheduleId: schedule.id,
        //             routeDate: dayjs(selectedDay, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        //             routeId: addForRouteId,
        //             driverId: Number(newDriverId),
        //             daysOnRoute: Number(newDays) || 0,
        //         }),
        //     });
        //     if (!res.ok) throw new Error(await res.text() || "Create failed");
        //     const created = await res.json();
        //     setRouteSchedules(prev => [...prev, created]);
        //     setAddOpen(false);
        // } catch (e) {
        //     setError(e.message || "Create failed");
        // }
    };

    const generate = async () => {
        if (!customerId) {
            setError("Please select a customer.");
            setTimeout(() => setError(""), 3000);
            return;
        }
        setLoading(true); setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch(
                generateApi(customerId, subCustomerId, selectedWeek ? dayjs(selectedWeek, 'MM/DD/YYYY').format('YYYY-MM-DD') : dayjs().subtract(7, 'day').format("YYYY-MM-DD")),
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` }
                });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            if (data.schedule) setSchedule(data.schedule);
            setInvoices(data.invoices || []);
            setOpenRows(new Set());
            setDetailsCache({});
        } catch (e) {
            setError(e.message || "Failed to generate invoices.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        let surchargeDetail = editing;

        if (surchargeDetail) {
            const token = await Session.getToken();
            const res = await fetch(
                deleteSurchargeApi(surchargeDetail.invoiceId, surchargeDetail.id),
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
        }
        setDeleteOpen(false);
    }

    // Persist an edited guarantee-days value on a route-less backup or lead line, then refresh with the
    // recomputed invoice so the standby/make-up/lead totals update.
    const saveGuaranteeDays = async (detail, value) => {
        const num = value === "" ? 0 : Number(value);
        if (isNaN(num) || num < 0) return;
        // Both backup and lead lines edit the guaranteed days (billed at the line's rate).
        const current = Number(detail.guaranteeDays ?? 5);
        if (num === current) return;
        try {
            const token = await Session.getToken();
            const res = await fetch('/api/invoicing/update-guarantee-days', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ invoiceId: detail.invoiceId, invoiceDetailId: detail.id, guaranteeDays: num })
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
        } catch (e) {
            setError(e.message || 'Failed to update guarantee days.');
        }
    };

    // Persist a single edited stored field on a base route line, then refresh with the recomputed
    // invoices so line and invoice totals update. Mirrors saveGuaranteeDays: parse, skip no-ops,
    // POST to the field's dedicated endpoint, and replace state with the returned invoices array.
    // Open the Change-Route modal for a base route line and load sibling routes for its sub-customer.
    const openChangeRoute = async (detail) => {
        setChangeRouteDetail(detail);
        setChangeRouteValue("");
        setChangeRouteOptions([]);
        setChangeRouteOpen(true);
        const subId = detail.route?.subCustomerId ?? detail.invoice?.subCustomerId;
        const currentRouteId = detail.routeId ?? detail.route?.id;
        if (!subId) return;
        setChangeRouteLoading(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/routes/BySubCustomer/${subId}`, { headers: { Authorization: `Bearer ${token}` } });
            const list = res.ok ? await res.json() : [];
            setChangeRouteOptions((list || []).filter(r => r.id !== currentRouteId)
                .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        } catch {
            setChangeRouteOptions([]);
        } finally {
            setChangeRouteLoading(false);
        }
    };

    const handleChangeRoute = async () => {
        if (!changeRouteDetail || !changeRouteValue) return;
        setChangeRouteSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch('/api/invoicing/update-line-route', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ invoiceId: changeRouteDetail.invoiceId, invoiceDetailId: changeRouteDetail.id, routeId: Number(changeRouteValue) })
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
            setChangeRouteOpen(false);
        } catch (e) {
            setError(e.message || 'Failed to change route.');
        } finally {
            setChangeRouteSubmitting(false);
        }
    };

    const saveStoredField = async (detail, url, field, value, current) => {
        const num = value === "" ? 0 : Number(value);
        if (isNaN(num) || num < 0) return;
        if (num === Number(current ?? 0)) return;
        try {
            const token = await Session.getToken();
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ invoiceId: detail.invoiceId, invoiceDetailId: detail.id, [field]: num })
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
        } catch (e) {
            setError(e.message || 'Failed to update value.');
        }
    };

    const saveDaysOnRoute = (detail, value) =>
        saveStoredField(detail, '/api/invoicing/update-days-on-route', 'daysOnRoute', value, detail.daysOnRoute);
    const saveBillRate = (detail, value) =>
        saveStoredField(detail, '/api/invoicing/update-bill-rate', 'billRate', value, detail.billRate);
    const saveExpectedDays = (detail, value) =>
        saveStoredField(detail, '/api/invoicing/update-expected-days', 'expectedDays', value, detail.expectedDays);
    // Note: Mileage & Hours are intentionally read-only in the grid — the recalc re-derives them from
    // the route daily schedule, so a manual edit would not persist. The backend update-mileage/
    // update-hours endpoints remain available if a persisted-override approach is added later.

    const handleSubmit = async () => {
        let detail = editing;

        if (detail) {
            const token = await Session.getToken();
            const res = await fetch(
                submitApi(detail.invoiceId),
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
        }
        setSubmitOpen(false);
    }
    //const toggleRow = async (invoiceId) => {
    //    const isOpen = openRows.has(invoiceId);
    //    const next = new Set(openRows);
    //    if (isOpen) {
    //        next.delete(invoiceId);
    //        setOpenRows(next);
    //        return;
    //    }
    //    // opening: if no cache, load details
    //    if (!detailsCache[invoiceId]) {
    //        setLoadingDetails((prev) => ({ ...prev, [invoiceId]: true }));
    //        try {
    //            const token = await Session.getToken();
    //            const res = await fetch(detailsApi(invoiceId), { headers: { Authorization: `Bearer ${token}` } });
    //            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    //            const details = await res.json();
    //            setDetailsCache((prev) => ({ ...prev, [invoiceId]: details }));
    //        } catch (e) {
    //            setError(e.message || "Failed to load invoice details.");
    //            setTimeout(() => setError(""), 4000);
    //        } finally {
    //            setLoadingDetails((prev) => ({ ...prev, [invoiceId]: false }));
    //        }
    //    }
    //    next.add(invoiceId);
    //    setOpenRows(next);
    //};

    const hasExisting = invoices && invoices.length > 0;

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Invoice Review</h5></Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={4}>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    setSelectedWeek(value);
                                    saveFinanceFilter("selectedWeek", value);
                                }}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>
                                        {w.displayDate}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={4}>
                            <SearchableDropdownFilter
                                value={customerId}
                                options={customers}
                                placeholder="Select Customer..."
                                getOptionValue={getOptionId}
                                getOptionLabel={getCustomerLabel}
                                onChange={(value) => {
                                    setCustomerId(value);
                                    setSubCustomerId('');
                                    saveFinanceFilter("customerId", value);
                                    saveFinanceFilter("subCustomerId", "");
                                    setInvoices([]);
                                    setFilteredInvoices([]);
                                }}
                            />
                        </Col>
                        <Col md={4}>
                            <SearchableDropdownFilter
                                value={subCustomerId}
                                options={subCustomerOptions}
                                placeholder="Select Sub-Customer..."
                                getOptionValue={getOptionId}
                                getOptionLabel={getSubCustomerLabel}
                                disabled={!customerId}
                                onChange={(value) => {
                                    setSubCustomerId(value);
                                    saveFinanceFilter("subCustomerId", value);
                                }}
                            />
                        </Col>
                    </Row>

                    {error && <Alert color="danger">{error}</Alert>}

                    {schedule && (
                        <Row className="mb-4">
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Payment Week</div>
                                        <div className="h5 mb-0">{fmt(schedule.startDate)} - {fmt(schedule.endDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Invoice Date</div>
                                        <div className="h5 mb-0">{fmt(schedule.invoiceDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Button
                                    color={hasExisting ? "warning" : "primary"}
                                    onClick={generate}
                                    disabled={!customerId || loading}
                                    title={hasExisting ? "Regenerate/refresh from Route Schedules" : "Generate for selected week"}
                                >
                                    {loading ? "Working..." : hasExisting ? "Refresh Invoice Data" : "Generate Invoice Data"}
                                </Button>
                            </Col>
                        </Row>
                    )}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <th>Sub-Customer</th>
                                    <th>Route</th>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Expected</th>
                                    <th>Mileage</th>
                                    <th>Hours</th>
                                    <th>Rate</th>
                                    <th>Line Total</th>
                                    <th>Invoice Total</th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(!invoices || invoices.length === 0) ? (
                                    <tr><td colSpan="13" className="text-center">No invoices for the prior week.</td></tr>
                                ) : (
                                    sortedFilteredInvoices.map((invoice, index) => {
                                        const firstRow = firstDetailIdBySubCustomer.get(invoice.invoice?.subCustomerId) === invoice.id;
                                        const firstRouteRow = firstDetailIdBySubCustomerRoute.get(`${invoice.invoice?.subCustomerId ?? ''}|${invoice.routeId ?? invoice.route?.id ?? ''}`) === invoice.id;
                                        const baseLine = isBaseLine(invoice);
                                        const overtimeLine = isOvertimeLine(invoice);
                                        const validationLine = isOvertimeCutoffValidationLine(invoice)
                                            || isFuelSurchargeValidationLine(invoice)
                                            || isZeroCalculatedFuelSurchargeLine(invoice);
                                        const hourlyBase = isHourlyBaseLine(invoice);
                                        // For Hourly routes, DaysOnRoute carries billable hours (not days), so a
                                        // hours-vs-days comparison against ExpectedDays is meaningless.
                                        const mismatch = validationLine || (baseLine && !hourlyBase && invoice.daysOnRoute !== invoice.expectedDays);
                                        const validated = firstRow ? validateInvoice(invoice) : true;

                                        return (
                                            <React.Fragment key={invoice.id}>
                                                <tr>
                                                    <td>{firstRouteRow ? (invoice.invoice?.subCustomer?.name ?? invoice.invoice?.subCustomerId) : ''}</td>
                                                    <td>
                                                        {firstRouteRow ? invoice.route?.name : ''}
                                                        {firstRouteRow && (invoice.invoicingNotes || invoice.paysheetNotes) && (
                                                            <Button size="sm" color="info" outline className="ms-2" title="View route notes"
                                                                onClick={() => { setNotesText(routeNotesText(invoice)); setNotesOpen(true); }}>
                                                                Notes
                                                            </Button>
                                                        )}
                                                        {firstRouteRow && (invoice.routeId ?? invoice.route?.id) && (
                                                            <Button size="sm" color="secondary" outline className="ms-2" title="Reassign this route (e.g. to its No Delivery version)"
                                                                onClick={() => openChangeRoute(invoice)}>
                                                                Change
                                                            </Button>
                                                        )}
                                                    </td>
                                                    <td>{isFalseTripLine(invoice) ? <Badge color="warning">False Trip</Badge> : isBackupDriverLine(invoice) ? <Badge color="info">Backup Driver</Badge> : isLeadDriverLine(invoice) ? <Badge color="primary">Lead Pay</Badge> : lineDescription(invoice)}</td>
                                                    <td style={{ textAlign: 'center' }} className={mismatch ? "table-danger fw-bold" : ''}>
                                                        {(isBackupDriverLine(invoice) || isLeadDriverLine(invoice)) ? (
                                                            <Input
                                                                key={`gd-${invoice.id}-${invoice.guaranteeDays ?? ''}`}
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="1"
                                                                style={{ width: 72, display: 'inline-block' }}
                                                                defaultValue={invoice.guaranteeDays ?? 5}
                                                                title={isBackupDriverLine(invoice) ? "Guaranteed backup days billed at the base rate (days × rate). Edit to override." : "Guaranteed management days billed at the lead rate."}
                                                                onBlur={(e) => saveGuaranteeDays(invoice, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : (baseLine && !validationLine) ? (
                                                            <Input
                                                                key={`dor-${invoice.id}-${invoice.daysOnRoute ?? ''}`}
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="0.01"
                                                                style={{ width: 80, display: 'inline-block' }}
                                                                defaultValue={Number(invoice.daysOnRoute ?? 0)}
                                                                title={hourlyBase ? "Billable hours for this route." : "Actual days on route (billed at the rate)."}
                                                                onBlur={(e) => saveDaysOnRoute(invoice, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : lineQuantity(invoice, validationLine, overtimeLine)}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {baseLine ? (hourlyBase ? "—" : (
                                                            <Input
                                                                key={`exp-${invoice.id}-${invoice.expectedDays ?? ''}`}
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="0.01"
                                                                style={{ width: 80, display: 'inline-block' }}
                                                                defaultValue={Number(invoice.expectedDays ?? 0)}
                                                                title="Expected days on route."
                                                                onBlur={(e) => saveExpectedDays(invoice, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        )) : ''}
                                                    </td>
                                                    {/* Mileage & Hours are recomputed from the route daily
                                                        schedule on every recalc, so they are read-only here
                                                        (a manual edit would be silently overwritten). */}
                                                    <td style={{ textAlign: 'center' }}>
                                                        {invoice.totalMileage ?? 0}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {Number(invoice.totalHours ?? 0).toFixed(2)}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {baseLine ? (
                                                            <Input
                                                                key={`rate-${invoice.id}-${invoice.billRate ?? ''}`}
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="0.01"
                                                                style={{ width: 100, display: 'inline-block' }}
                                                                defaultValue={Number(invoice.billRate ?? 0)}
                                                                title="Bill rate (per day / per hour)."
                                                                onBlur={(e) => saveBillRate(invoice, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : `$${rateMoney(invoice)}`}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>${money(lineTotal(invoice))}</td>
                                                    <td style={{ textAlign: 'right' }}>{firstRow && `$${money(displayTotalByInvoiceId.get(invoice.invoiceId) ?? 0)}`}</td>
                                                    <td className="text-center">
                                                        {baseLine && (
                                                            <Button size="sm" color="success" onClick={() => openAdd(invoice, invoice.invoice?.subCustomerId)}>+</Button>
                                                        )}
                                                        {isAdjustmentLine(invoice) && (
                                                            <Button size="sm" color="info" className="me-1" title="Edit this adjustment" onClick={() => { setAdjustmentEditing(invoice); setAdjustmentInvoiceId(invoice.invoiceId); setAdjustmentOpen(true); }}>Edit</Button>
                                                        )}
                                                        {!firstRow && (invoice.surcharge !== null || isAdjustmentLine(invoice)) && (
                                                            <Button size="sm" color="warning" onClick={() => openDelete(invoice)}>-</Button>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {firstRouteRow && invoice.route && (
                                                            <Button size="sm" color="danger" title="Remove this route from the invoice" onClick={() => openRemoveRoute(invoice)}>Remove</Button>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {firstRow && (
                                                            <>
                                                                <Button size="sm" color="info" className="me-1" title="Add an adjustment line item" onClick={() => { setAdjustmentEditing(null); setAdjustmentInvoiceId(invoice.invoiceId); setAdjustmentOpen(true); }}>Adjustment</Button>
                                                                <Button size="sm" color={validated ? "success" : "warning"} onClick={() => openSubmit(invoice)}>Submit</Button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
            <Modal isOpen={addOpen} toggle={() => setAddOpen(false)} size="lg">
                <ModalHeader toggle={() => setAddOpen(false)}>Add Surcharge</ModalHeader>
                <ModalBody>
                    <InvoiceDetailForm
                        detailItem={editing}
                        subCustomerId={editingSubCustomerId}
                        apiBaseUrl={''}
                        onSuccess={(data) => { setInvoices(data); setAddOpen(false); }}
                        onCancel={() => { setAddOpen(false); }} />
                </ModalBody>
            </Modal>
            <Modal isOpen={adjustmentOpen} toggle={() => setAdjustmentOpen(false)} size="lg">
                <ModalHeader toggle={() => setAdjustmentOpen(false)}>{adjustmentEditing ? "Edit Adjustment" : "Add Adjustment"}</ModalHeader>
                <ModalBody>
                    <InvoiceAdjustmentForm
                        key={adjustmentEditing ? `edit-${adjustmentEditing.id}` : 'add'}
                        invoiceId={adjustmentInvoiceId}
                        adjustment={adjustmentEditing}
                        routes={Array.from(new Map(
                            invoices
                                .filter(d => `${d.invoiceId}` === `${adjustmentInvoiceId}` && d.routeId != null)
                                .map(d => [d.routeId, { routeId: d.routeId, name: d.route?.name ?? `Route ${d.routeId}` }])
                        ).values())}
                        onSuccess={(data) => { setInvoices(data); setAdjustmentOpen(false); }}
                        onCancel={() => setAdjustmentOpen(false)}
                    />
                </ModalBody>
            </Modal>
            <Modal isOpen={deleteOpen} toggle={() => setDeleteOpen(false)}>
                <ModalHeader toggle={() => setDeleteOpen(false)}>{isAdjustmentLine(editing) ? "Remove Adjustment" : "Remove Surcharge"}</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="driverSel">This will remove the selected {isAdjustmentLine(editing) ? "adjustment" : "surcharge"}, do you want to continue?</Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
                        <Button color="warning" onClick={handleDelete}>Remove</Button>
                    </div>
                </ModalBody>
            </Modal>
            <Modal isOpen={removeRouteOpen} toggle={() => !removeRouteSubmitting && setRemoveRouteOpen(false)}>
                <ModalHeader toggle={() => !removeRouteSubmitting && setRemoveRouteOpen(false)}>Remove Route From Invoice</ModalHeader>
                <ModalBody>
                    <Alert color="danger">
                        <strong>Are you sure you want to remove {editing?.route?.name ? `"${editing.route.name}"` : "this route"} from the invoice?</strong>
                        {" "}This removes all of its lines (base, additional drive time, and surcharges) and prevents any driver
                        pay sheet data from being created for this route for the week. The action is recorded with your name and the time.
                    </Alert>
                    <FormGroup>
                        <Label for="removeRouteReasonInput">Reason (optional)</Label>
                        <Input
                            id="removeRouteReasonInput"
                            type="textarea"
                            rows={3}
                            maxLength={2000}
                            placeholder="Why is this route being removed? (e.g. billed by mistake)"
                            value={removeRouteReason}
                            onChange={(e) => setRemoveRouteReason(e.target.value)}
                            disabled={removeRouteSubmitting}
                        />
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setRemoveRouteOpen(false)} disabled={removeRouteSubmitting}>Cancel</Button>
                        <Button color="danger" onClick={handleRemoveRoute} disabled={removeRouteSubmitting}>
                            {removeRouteSubmitting ? "Removing…" : "Remove Route"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={notesOpen} toggle={() => setNotesOpen(false)}>
                <ModalHeader toggle={() => setNotesOpen(false)}>Route Notes</ModalHeader>
                <ModalBody>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{notesText || "(no notes)"}</div>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button color="secondary" onClick={() => setNotesOpen(false)}>Close</Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={changeRouteOpen} toggle={() => !changeRouteSubmitting && setChangeRouteOpen(false)}>
                <ModalHeader toggle={() => !changeRouteSubmitting && setChangeRouteOpen(false)}>Change Route</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Reassign <strong>{changeRouteDetail?.route?.name}</strong> to:</Label>
                        {changeRouteLoading ? (
                            <div className="text-center py-2"><Spinner size="sm" /></div>
                        ) : (
                            <Input type="select" value={changeRouteValue} onChange={(e) => setChangeRouteValue(e.target.value)}>
                                <option value="">-- Select Route --</option>
                                {changeRouteOptions.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </Input>
                        )}
                    </FormGroup>
                    <div className="text-muted small mb-2">
                        Switching to a “No Delivery” route removes its fuel surcharge. This applies to all drivers on
                        the route for the week; regenerate the linked paysheets afterward so they match.
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setChangeRouteOpen(false)} disabled={changeRouteSubmitting}>Cancel</Button>
                        <Button color="primary" onClick={handleChangeRoute} disabled={changeRouteSubmitting || !changeRouteValue}>
                            {changeRouteSubmitting ? <><Spinner size="sm" /> Changing…</> : 'Change Route'}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={submitOpen} toggle={() => setSubmitOpen(false)}>
                <ModalHeader toggle={() => setSubmitOpen(false)}>Submit Invoice</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="driverSel">This will submit the selected invoice for approval, do you want to continue?</Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setSubmitOpen(false)}>Cancel</Button>
                        <Button color="primary" onClick={handleSubmit}>Submit</Button>
                    </div>
                </ModalBody>
            </Modal>

            {/* Validation Override Modal */}
            <Modal isOpen={overrideOpen} toggle={() => !overrideSubmitting && setOverrideOpen(false)} size="lg">
                <ModalHeader toggle={() => !overrideSubmitting && setOverrideOpen(false)}>
                    Submit Invoice — Validation Override
                </ModalHeader>
                <ModalBody>
                    <Alert color="warning">
                        <strong>This invoice has one or more validation errors.</strong> Submitting it will bypass those
                        checks. An audit record will be saved with your name, the timestamp, and the reason you provide below.
                    </Alert>
                    {editing && (() => {
                        const issues = invoices
                            .filter(d => d.invoiceId === editing.invoiceId)
                            .reduce((acc, d) => {
                                if (isFuelSurchargeValidationLine(d) || isZeroCalculatedFuelSurchargeLine(d))
                                    acc.add("Fuel surcharge rate is missing or zero for one or more routes.");
                                if (isOvertimeCutoffValidationLine(d))
                                    acc.add("Additional drive time cutoff is not configured for one or more routes.");
                                if (isBaseLine(d) && !isHourlyBaseLine(d) && d.daysOnRoute !== d.expectedDays)
                                    acc.add("Actual days on route do not match expected days for one or more routes.");
                                return acc;
                            }, new Set());
                        return issues.size > 0 ? (
                            <div className="mb-3">
                                <div className="fw-bold mb-1">Validation issues found:</div>
                                <ul className="mb-0">
                                    {[...issues].map((msg, i) => <li key={i}>{msg}</li>)}
                                </ul>
                            </div>
                        ) : null;
                    })()}
                    <FormGroup>
                        <Label for="overrideReasonInput">
                            Reason for overriding validation <span className="text-danger">*</span>
                        </Label>
                        <Input
                            id="overrideReasonInput"
                            type="textarea"
                            rows={4}
                            maxLength={2000}
                            placeholder="Describe why this invoice is being submitted despite validation errors..."
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            disabled={overrideSubmitting}
                        />
                        <div className="text-muted small mt-1">{overrideReason.length} / 2000</div>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setOverrideOpen(false)} disabled={overrideSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            color="warning"
                            onClick={handleSubmitWithOverride}
                            disabled={!overrideReason.trim() || overrideSubmitting}
                        >
                            {overrideSubmitting ? "Submitting…" : "Override & Submit"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

        </Container>
    );
};

export default InvoiceReview;
